import { useState } from "react"
import { toast } from "react-toastify"
import { LockClosedIcon, LockOpenIcon } from "@heroicons/react/24/outline"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"
import { useBudgetRecommenderControllerUpsertRowSettingMutation } from "~/ui-rtk/api/budgetRecommenderApi"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { describeLock } from "../budget-utils"
import type { RecommendationRow } from "../types"

const todayLocal = () => {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * The pin / unpin dialog. Mounted only while open, so the table renders
 * without the API (and its store) until someone actually pins.
 */
export function PinRowDialog({
  row,
  onClose,
}: {
  row: RecommendationRow
  onClose: () => void
}) {
  const [reason, setReason] = useState("")
  const [expiryDate, setExpiryDate] = useState("")
  const [upsertRowSetting, { isLoading }] =
    useBudgetRecommenderControllerUpsertRowSettingMutation()
  const pinned = row.lock?.source === "pin"

  const save = async (pin: boolean) => {
    try {
      await upsertRowSetting({
        entityType: row.entityType,
        entityId: row.entityId,
        pinned: pin,
        ...(pin
          ? {
              pinReason: reason.trim(),
              // A bare date: the API lapses it at the end of that day in Denver, like the CLI / MCP pin.
              pinExpiresAt: expiryDate || null,
            }
          : {}),
      }).unwrap()
      toast.success(
        pin ? `Pinned ${row.entityName}` : `Unpinned ${row.entityName}`,
      )
      onClose()
    } catch (error) {
      console.error("Failed to save the pin:", error)
      toast.error(pin ? "Failed to pin the row" : "Failed to unpin the row")
    }
  }

  return (
    <Dialog
      isOpen
      handleClose={onClose}
      width={480}
      classes={{
        container:
          "bg-basic-dark-blue border border-basic-blue rounded-xl p-0 text-left flex flex-col",
      }}
    >
      <div className="px-6 py-4 border-b border-basic-blue">
        <h2 className="text-lg font-semibold text-white">
          {pinned ? "Unpin row" : "Pin row"}
        </h2>
        <p className="text-sm text-gray-400 truncate" title={row.entityName}>
          {row.entityName}
        </p>
      </div>

      <div className="px-6 py-4 space-y-4">
        {pinned && row.lock ? (
          <p className="text-sm text-gray-300">{describeLock(row.lock)}</p>
        ) : (
          <>
            <p className="text-sm text-gray-300">
              A pinned row stays at its current budget in every recommendation
              until you unpin it: it is never selected, scaled or paused, and
              its budget counts toward its category&apos;s share. This moves no
              money.
            </p>
            <label className="block text-sm text-gray-300">
              Reason
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                maxLength={500}
                rows={2}
                placeholder="e.g. brand-search holdout, test cell"
                className="mt-1 w-full px-2 py-1.5 rounded-lg border border-basic-blue bg-basic-black text-white text-sm"
              />
            </label>
            <label className="block text-sm text-gray-300">
              Pinned through (optional)
              <input
                type="date"
                value={expiryDate}
                min={todayLocal()}
                onChange={e => setExpiryDate(e.target.value)}
                className="mt-1 block px-2 py-1.5 rounded-lg border border-basic-blue bg-basic-black text-white text-sm"
              />
              <span className="text-xs text-gray-500">
                Leave empty to pin until unpinned.
              </span>
            </label>
          </>
        )}
      </div>

      <div className="px-6 py-4 border-t border-basic-blue flex justify-end gap-3">
        <Button variant={{ variant: "outlined" }} size="sm" onClick={onClose}>
          Cancel
        </Button>
        {pinned ? (
          <Button
            variant={{ variant: "solid", color: "blue" }}
            size="sm"
            disabled={isLoading}
            onClick={() => void save(false)}
          >
            Unpin
          </Button>
        ) : (
          <Button
            variant={{ variant: "solid", color: "blue" }}
            size="sm"
            disabled={isLoading || reason.trim() === ""}
            onClick={() => void save(true)}
          >
            Pin
          </Button>
        )}
      </div>
    </Dialog>
  )
}

/**
 * Pin or unpin one row (APP 539). A pin is saved on the row's settings, so
 * every later load — the page and budget_recommend — holds the row at its
 * current budget until it is unpinned or its expiry passes. Moves no money:
 * the live budget is untouched. The API writes the audit row.
 */
export default function PinRowControl({ row }: { row: RecommendationRow }) {
  const [isOpen, setIsOpen] = useState(false)
  const pinned = row.lock?.source === "pin"

  return (
    <>
      <button
        type="button"
        aria-label={pinned ? "Unpin row" : "Pin row"}
        title={
          pinned
            ? "Pinned: the recommender holds this row at its current budget. Click to unpin."
            : "Pin: hold this row at its current budget in every recommendation"
        }
        className={cn(
          "btn btn-ghost btn-xs px-1 shrink-0",
          pinned
            ? "text-violet-300 hover:text-violet-200"
            : "text-gray-500 hover:text-gray-300",
        )}
        onClick={e => {
          e.stopPropagation()
          setIsOpen(true)
        }}
      >
        {pinned ? (
          <LockClosedIcon className="w-4 h-4" />
        ) : (
          <LockOpenIcon className="w-4 h-4" />
        )}
      </button>
      {isOpen && <PinRowDialog row={row} onClose={() => setIsOpen(false)} />}
    </>
  )
}
